import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { CancelButton } from '@/components/ui/CancelButton';
import { Input } from '@/components/ui/Input';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { BulkImportDropzone } from './BulkImportDropzone';
import { BulkImportProgress } from './BulkImportProgress';
import { BulkImportErrors } from './BulkImportErrors';
import { BulkImportStartWarnings } from './BulkImportStartWarnings';
import {
  useStartBulkImport,
  useBulkImportStatus,
  useCancelBulkImport,
  downloadTemplate,
} from '@/hooks/useBulkImport';
import { BulkImportOptions } from '@/types/bulkImport';
import { toast } from '@/utils/toast';
import { useNavigate } from 'react-router-dom';
import { formatApiError } from '@/services/api';
import { useBulkImportStore } from '@/stores/bulkImportStore';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';

/**
 * BulkImportDialog Component
 *
 * Multi-step modal for bulk post import:
 * 1. Upload - Select CSV/Excel file
 * 2. Options - Optional defaults
 * 3. Progress - Real-time import progress
 * 4. Complete - Summary and errors
 */

interface BulkImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  importType?: 'posts' | 'variations';
}

type Step = 'upload' | 'options' | 'progress' | 'complete';

export const BulkImportDialog = ({
  isOpen,
  onClose,
  onSuccess,
  importType = 'posts',
}: BulkImportDialogProps) => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [jobId, setJobId] = useState<number | null>(null);
  // Notes returned when the import started (kept with the dialog only, not in the background store)
  const [startWarnings, setStartWarnings] = useState<string[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Options state
  const [options, setOptions] = useState<BulkImportOptions>({
    defaultLabels: [],
    defaultHashtags: [],
    defaultChannelIds: [],
  });
  const [labelsInput, setLabelsInput] = useState('');
  const [hashtagsInput, setHashtagsInput] = useState('');

  useUnsavedGuard(
    isOpen && (selectedFile !== null || labelsInput.trim().length > 0 || hashtagsInput.trim().length > 0)
  );

  // Mutations and queries
  const startImport = useStartBulkImport();
  const { data: jobStatus, isLoading: isLoadingStatus } = useBulkImportStatus(jobId);
  const cancelImport = useCancelBulkImport();

  // Guard: prevent re-submission while a background import is still running
  const activeImport = useBulkImportStore((s) => s.activeImport);
  const clearBackgroundImport = useBulkImportStore((s) => s.clearBackgroundImport);
  const TERMINAL_STATUSES_SET = new Set(['COMPLETED', 'FAILED', 'CANCELLED']);
  const isImportActive =
    activeImport !== null &&
    (activeImport.status === null ||
      !TERMINAL_STATUSES_SET.has(activeImport.status.status));


  /**
   * Auto-advance to complete step when job finishes
   * Also trigger posts refresh on completion
   */
  useEffect(() => {
    if (jobStatus && step === 'progress') {
      if (
        jobStatus.status === 'COMPLETED' ||
        jobStatus.status === 'FAILED' ||
        jobStatus.status === 'CANCELLED'
      ) {
        setStep('complete');

        // Auto-refresh posts when import completes successfully
        if (jobStatus.status === 'COMPLETED' && jobStatus.successCount > 0) {
          onSuccess?.();
        }
      }
    }
  }, [jobStatus, step, onSuccess]);

  /**
   * Reset state when dialog closes
   */
  useEffect(() => {
    if (!isOpen) {
      // Small delay to allow closing animation
      setTimeout(() => {
        setStep('upload');
        setSelectedFile(null);
        setJobId(null);
        setStartWarnings([]);
        setUploadError(null);
        setOptions({
          defaultLabels: [],
          defaultHashtags: [],
          defaultChannelIds: [],
        });
        setLabelsInput('');
        setHashtagsInput('');
      }, 300);
    }
  }, [isOpen]);


  /**
   * Handle file selection
   */
  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    setUploadError(null);
  };

  /**
   * Handle file removal
   */
  const handleFileRemove = () => {
    setSelectedFile(null);
    setUploadError(null);
  };

  /**
   * Handle next from upload step
   */
  const handleNextFromUpload = () => {
    if (!selectedFile) {
      setUploadError(t('please_select_file'));
      return;
    }
    // Skip options step for variations import
    if (importType === 'variations') {
      handleStartImport();
    } else {
      setStep('options');
    }
  };

  /**
   * Handle start import
   */
  const handleStartImport = async () => {
    if (!selectedFile) return;

    // Parse options
    const parsedOptions: BulkImportOptions = {
      defaultLabels: labelsInput
        ? labelsInput.split(',').map((s) => s.trim()).filter(Boolean)
        : [],
      defaultHashtags: hashtagsInput
        ? hashtagsInput.split(',').map((s) => s.trim()).filter(Boolean)
        : [],
      defaultChannelIds: options.defaultChannelIds,
    };

    try {
      const result = await startImport.mutateAsync({
        file: selectedFile,
        options: parsedOptions,
        importType,
      });

      setJobId(result.jobId);
      setStartWarnings(result.warnings ?? []);
      setStep('progress');
    } catch (error) {
      setUploadError(formatApiError(error).message);
    }
  };

  /**
   * Handle cancel import
   */
  const handleCancel = async () => {
    if (!jobId) return;

    try {
      await cancelImport.mutateAsync(jobId);
    } catch (error) {
      toast.error(t('failed_cancel_import'));
    }
  };

  /**
   * Handle close dialog
   */
  const handleClose = () => {
    onClose();

    // Call onSuccess if import completed successfully
    if (step === 'complete' && jobStatus?.status === 'COMPLETED') {
      onSuccess?.();
    }
  };

  /**
   * Handle download template
   */
  const handleDownloadTemplate = async (format: 'csv' | 'xlsx') => {
    await downloadTemplate(format, importType);
  };

  /**
   * Handle view posts
   */
  const handleViewPosts = () => {
    onClose();
    onSuccess?.();
    navigate('/creator');
  };

  /**
   * Render step content
   */
  const renderStepContent = () => {
    switch (step) {
      case 'upload':
        return (
          <div className="space-y-6">
            {/* Instructions */}
            <div className="bg-soft-mint border border-light-teal-gray rounded-lg p-4">
              <div className="flex gap-3">
                <Icon
                  name={ICONS.INFO}
                  size="sm"
                  color="text-primary-700"
                  className="flex-shrink-0 mt-0.5"
                  ariaHidden
                />
                <div className="text-sm text-dark-teal">
                  {importType === 'variations' ? (
                    <>
                      <p className="font-medium mb-1">{t('how_to_use_variation_import')}</p>
                      <ol className="list-decimal list-inside space-y-1">
                        <li>{t('variation_step_1')}</li>
                        <li>{t('variation_step_2')}</li>
                        <li>{t('variation_step_3')}</li>
                      </ol>
                    </>
                  ) : (
                    <>
                      <p className="font-medium mb-1">{t('how_to_use_bulk_import')}</p>
                      <ol className="list-decimal list-inside space-y-1">
                        <li>{t('download_template_step')}</li>
                        <li>{t('fill_data_step')}</li>
                        <li>{t('upload_file_step')}</li>
                      </ol>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Download Template */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('download_template')}
              </label>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleDownloadTemplate('csv')}
                  className="flex items-center gap-2"
                >
                  <Icon name={ICONS.DOWNLOAD} size="sm" ariaHidden />
                  {t('csv_template')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleDownloadTemplate('xlsx')}
                  className="flex items-center gap-2"
                >
                  <Icon name={ICONS.DOWNLOAD} size="sm" ariaHidden />
                  {t('excel_template')}
                </Button>
              </div>
            </div>

            {/* Active import guard notice */}
            {isImportActive && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
                <Icon
                  name={ICONS.CAUTION}
                  size="sm"
                  color="text-amber-600"
                  className="flex-shrink-0 mt-0.5"
                  ariaHidden
                />
                <div className="text-sm text-amber-800">
                  {/* eslint-disable-next-line i18next/no-literal-string */}
                  You already have an import in progress. Wait for it to finish, or{' '}
                  <button
                    type="button"
                    className="underline font-medium hover:text-amber-900"
                    onClick={clearBackgroundImport}
                  >
                    refresh status
                  </button>
                  .
                </div>
              </div>
            )}

            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('upload_file')}
              </label>
              <BulkImportDropzone
                onFileSelect={handleFileSelect}
                onFileRemove={handleFileRemove}
                selectedFile={selectedFile}
                error={uploadError}
              />
            </div>
          </div>
        );

      case 'options':
        return (
          <div className="space-y-6">
            {/* Instructions */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <p className="text-sm text-gray-600">
                <strong>{t('optional')}:</strong> {t('optional_defaults')}
              </p>
            </div>

            {/* Default Labels */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('default_labels')}
              </label>
              <Input
                type="text"
                placeholder={t('labels_placeholder')}
                value={labelsInput}
                onChange={(e) => setLabelsInput(e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500">
                {t('separate_labels')}
              </p>
            </div>

            {/* Default Hashtags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('default_hashtags')}
              </label>
              <Input
                type="text"
                placeholder={t('hashtags_placeholder')}
                value={hashtagsInput}
                onChange={(e) => setHashtagsInput(e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500">
                {t('separate_hashtags_auto')}
              </p>
            </div>

            {/* Note about drafts */}
            <div className="bg-soft-mint border border-light-teal-gray rounded-lg p-4">
              <p className="text-sm text-dark-teal">
                {/* eslint-disable-next-line i18next/no-literal-string */}
                <strong>Tip:</strong> {t('posts_as_drafts_tip')}
              </p>
            </div>
          </div>
        );

      case 'progress':
        return (
          <div>
            <BulkImportStartWarnings warnings={startWarnings} />
            {isLoadingStatus && !jobStatus ? (
              <div className="text-center py-8">
                <div className="animate-spin mx-auto mb-4">
                  <Icon name={ICONS.CLOCK} size="lg" color="text-primary-600" ariaHidden />
                </div>
                <p className="text-sm text-gray-600">{t('loading_import_status')}</p>
              </div>
            ) : jobStatus ? (
              <BulkImportProgress
                status={jobStatus}
                onCancel={handleCancel}
                isCancelling={cancelImport.isPending}
              />
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-red-600">{t('failed_load_status')}</p>
              </div>
            )}
          </div>
        );

      case 'complete':
        return (
          <div className="space-y-6">
            {/* Summary */}
            {jobStatus && (
              <div>
                {jobStatus.status === 'COMPLETED' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                    <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                      <Icon name={ICONS.CHECK} size="lg" color="text-green-600" ariaHidden />
                    </div>
                    <h3 className="text-lg font-semibold text-green-900 mb-2">
                      {t('import_completed_success')}
                    </h3>
                    <p className="text-sm text-green-700">
                      {jobStatus.importType === 'VARIATIONS'
                        ? t('variations_created_count', { count: jobStatus.successCount })
                        : t('posts_imported_count', { count: jobStatus.successCount })
                      }
                      {jobStatus.failureCount > 0 &&
                        ` ${t('failed_count', { count: jobStatus.failureCount })}`}
                    </p>
                  </div>
                )}

                {jobStatus.status === 'FAILED' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                    <div className="mx-auto w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                      <Icon name={ICONS.CAUTION} size="lg" color="text-red-600" ariaHidden />
                    </div>
                    <h3 className="text-lg font-semibold text-red-900 mb-2">
                      {t('import_failed')}
                    </h3>
                    <p className="text-sm text-red-700">
                      {t('import_failed_description')}
                    </p>
                  </div>
                )}

                {jobStatus.status === 'CANCELLED' && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
                    <div className="mx-auto w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <Icon name={ICONS.CLOSE} size="lg" color="text-gray-600" ariaHidden />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {t('import_cancelled')}
                    </h3>
                    <p className="text-sm text-gray-700">
                      {t('import_cancelled_description', { count: jobStatus.successCount })}
                    </p>
                  </div>
                )}

                {/* Errors */}
                {jobStatus.errors && jobStatus.errors.length > 0 && (
                  <BulkImportErrors errors={jobStatus.errors} />
                )}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  /**
   * Render footer buttons
   */
  const renderFooter = () => {
    switch (step) {
      case 'upload':
        return (
          <div className="flex justify-end gap-1.5 sm:gap-2">
            <CancelButton onClick={handleClose} tooltip="Cancel" />
            <Button
              variant="primary"
              onClick={handleNextFromUpload}
              disabled={!selectedFile || isImportActive}
            >
              {t('next')}
            </Button>
          </div>
        );

      case 'options':
        return (
          <div className="flex justify-between gap-1.5 sm:gap-2">
            <Button variant="secondary" onClick={() => setStep('upload')}>
              {t('back')}
            </Button>
            <div className="flex gap-1.5 sm:gap-2">
              <CancelButton onClick={handleClose} tooltip="Cancel" />
              <Button
                variant="primary"
                onClick={handleStartImport}
                disabled={startImport.isPending || isImportActive}
              >
                {startImport.isPending ? t('starting') : t('start_import')}
              </Button>
            </div>
          </div>
        );

      case 'progress':
        return (
          <div className="flex justify-end gap-1.5 sm:gap-2">
            <CancelButton onClick={handleClose} tooltip="Close" />
          </div>
        );

      case 'complete':
        return (
          <div className="flex justify-end gap-1.5 sm:gap-2">
            {jobStatus?.status === 'COMPLETED' && jobStatus.successCount > 0 && (
              <Button variant="primary" onClick={handleViewPosts}>
                <Icon name={ICONS.MAGIC_WAND} size="sm" ariaHidden />
                {t('view_posts')}
              </Button>
            )}
            <Button
              variant={
                jobStatus?.status === 'COMPLETED' && jobStatus.successCount > 0
                  ? 'secondary'
                  : 'primary'
              }
              onClick={handleClose}
            >
              {t('done')}
            </Button>
          </div>
        );

      default:
        return null;
    }
  };

  /**
   * Get step title
   */
  const getStepTitle = () => {
    switch (step) {
      case 'upload':
        return importType === 'variations' ? t('import_variations') : t('bulk_import_posts');
      case 'options':
        return t('import_options');
      case 'progress':
        return importType === 'posts' ? t('importing_posts') : t('importing_variations');
      case 'complete':
        return t('import_complete');
      default:
        return t('bulk_import');
    }
  };

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && handleClose()}
      title={getStepTitle()}
      size="lg"
      footer={renderFooter()}
    >
      {renderStepContent()}
    </Modal>
  );
};
